<template>
  <div>
    <div class="grid-body p-2" v-for="(order, index) in orders" :key="index">
      <div class="col-md-9">
        <h5 class="text-bold">Order Reference #</h5>
        <h4 class="text-bold">
          {{ getOrderReference(index) }}
          <button class="btn btn-primary" @click="editOrderReference(index)">
            <span class="fa fa-pencil"></span>
          </button>
        </h4>
      </div>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Fabric</th>
            <th class="text-center">Order Amount</th>
            <th class="text-center">Unit</th>
            <th class="text-center">Unit Price</th>
            <th class="text-center">VAT</th>
            <th class="text-center">Total Unit Price ({{ order.currency }})</th>
            <th class="text-center">Currency</th>
            <th class="text-center">
              Total Order Price ({{ order.currency }})
            </th>
            <th class="text-center">ETA</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(f, fabricIndex) in order.fabrics" :key="fabricIndex">
            <td>{{ f.production_name }}</td>
            <td width="150">
              <money
                v-model="f.amount"
                class="form-control text-right"
                v-bind="money"
                :on-change="calculateOrderAmount(order)"
              />
            </td>
            <td width="200">
              <input v-if="f.is_jersey"
                type="text"
                v-model="f.default_unit"
                readonly
                class="form-control text-right"
              />
              <v-select v-if="!f.is_jersey"
                placeholder="Choose One"
                v-model="f.default_unit"
                :reduce="(unit) => unit.value"
                :options="[
                  { value: 'm', label: 'Meter' },
                  { value: 'y', label: 'Yard' },
                  { value: 'kg', label: 'Kilogram' },
                ]"
                :on-change="calculateOrderAmount(order)"
                label="label"
                class="unit"
              />
            </td>
            <td class="text-right" width="150">
              <money
                v-model="f.default_unit_price"
                class="form-control text-right"
                @change="calculateOrderAmount(order)"
                v-bind="money"
              />
            </td>
            <td width="150">
              <money
                v-model="f.default_vat_per_unit"
                class="form-control text-right"
                @change="calculateOrderAmount(order)"
                v-bind="money"
              />
            </td>
            <td class="text-right">
              <money
                v-model="f.default_unit_price_total"
                class="form-control text-right"
                readonly
                v-bind="money"
              />
            </td>
            <td class="text-center">
              {{ order.currency }}
            </td>
            <td class="text-right">
              {{ ezy.comma(f.amount * f.default_unit_price_total) }}
            </td>
            <td class="text-right">
              <input
                type="date"
                v-model="f.next_order_eta"
                class="form-control text-right"
              />
            </td>
            <td class="text-right">
              <button
                class="config btn btn-primary"
                @click="showConfigurationModal(index, fabricIndex)"
              >
                <i class="fa fa-gears" aria-hidden="true"></i> Config
              </button>
              <button
                class="btn btn-danger"
                @click="deleteFabric(index, fabricIndex)"
              >
                <i class="fa fa-trash" aria-hidden="true"></i>
              </button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="text-bold">Total: {{ order.fabrics.length }}</td>
            <td class="text-bold text-right">
              {{ ezy.comma(order.total_amount) }}
            </td>
            <td class="text-bold">Meter</td>
            <td class="text-bold"></td>
            <td class="text-bold"></td>
            <td class="text-bold"></td>
            <td class="text-bold text-right">
              {{ ezy.comma(order.total_price) }}
            </td>
            <td>
              <v-select
                :options="['THB', 'USD']"
                :on-change="updateFabricCurrency(order)"
                v-model="order.currency"
                placeholder="Choose Currency"
              />
            </td>
          </tr>
        </tfoot>
      </table>
      <div class="grid-body">
        <button
          class="btn btn-block btn-primary"
          @click="$emit('create-order', index)"
        >
          Create Order
        </button>
      </div>
    </div>

    <FabricConfigurationModal
      v-if="configModal.displayed"
      @component-updated="handleModalUpdate"
      @hide-modal="hideConfigurationModal"
      @save-modal="saveConfigurationModal"
      :fieldValues="fieldValues"
      :fabric="orders[configModal.orderIndex].fabrics[configModal.fabricIndex]"
    />
  </div>
</template>


<script>
import FabricConfigurationModal from "./FabricConfigurationModal";
import { Money } from "v-money";

export default {
  props: ["orders", "fieldValues"],
  data() {
    return {
      configModal: {
        displayed: false,
        fabricIndex: null,
        orderIndex: null,
      },
      oldConfigValues: {
        type: [Object],
        default: null,
      },
      money: {
        decimal: ".",
        thousands: ",",
        prefix: "",
        suffix: "",
        precision: 2,
        masked: false,
      },
    };
  },
  components: {
    FabricConfigurationModal,
    Money
  },
  methods: {
    getOrderReference(index) {
      let order = this.orders[index];
      let orderId =
        order.nextOrderId +
        "-" +
        moment(order.invoice_date).format("YYYY/MM/DD") +
        "-" +
        order.supplier.code;
      orderId += order.postfix !== "" ? "-" + order.postfix : "";
      this.orders[index].order_reference = orderId;
      return orderId;
    },

    async editOrderReference(index) {
      let order = this.orders[index];
      await Swal.fire({
        title: "Add a postfix to the Order Reference",
        input: "text",
        inputValue: order.postfix,
        showCancelButton: true,
        inputValidator: (value) => {
          this.orders[index].postfix = value;
        },
      });
    },
    /**
     * Calculate the total amount of order for one supplier
     */
    calculateOrderAmount(order) {
      order.total_amount = 0;
      order.fabrics.forEach((fabric) => {

        let amount = 0;
        if (fabric.default_unit === "y") {
          amount = fabric.amount * 0.9144;
        }
        if (fabric.default_unit === "kg") {
          amount = this.getJerseyLengthEstimationInMeter(fabric);
        } else {
          amount = fabric.amount * 1;
        }
        order.total_amount += amount;
        fabric.default_unit_price_total =
          parseFloat(fabric.default_unit_price) +
          parseFloat(fabric.default_vat_per_unit);
      });
      this.calculateTotalOrderPrice(order);
    },

    /**
     * Calculate the total price of order for one supplier (VAT included)
     */
    calculateTotalOrderPrice(order) {
      let total = 0;
      order.fabrics.forEach((fabric) => {
        total +=
          fabric.amount *
          (parseFloat(fabric.default_unit_price) +
            parseFloat(fabric.default_vat_per_unit));
      });
      order.total_price = total;
    },
    /**
     * Update all fabrics currencies if the order currency change
     */
    updateFabricCurrency(order) {
      order.fabrics.forEach((fabric) => {
        fabric.default_currency = order.currency;
      });
    },

    deleteFabric(index, fabricIndex) {
      this.orders[index].fabrics.splice(fabricIndex, 1);
      if (this.orders[index].fabrics.length === 0) {
        this.orders.splice(index, 1);
      }
    },

    showConfigurationModal(index, fabricIndex) {
      this.configModal = {
        displayed: true,
        orderIndex: index,
        fabricIndex: fabricIndex,
      };

      this.oldConfigValues = {
        ...{},
        ...this.orders[index].fabrics[fabricIndex],
      };

      setTimeout(function () {
        // hack: needs to be in a setTimeout to give the DOM enough
        // time to init the modal because ... jquery!
        show_modal("#configuration-modal");
      }, 250);
    },
    /**
     * This method is called when the user click on the [X] button of the modal
     */
    hideConfigurationModal() {
      // Reset back fabric configuration to the old values
      for (var key in this.oldConfigValues) {
        this.orders[this.configModal.orderIndex].fabrics[
          this.configModal.fabricIndex
        ][key] = this.oldConfigValues[key];
      }
      hide_modal("#configuration-modal");
    },

    /**
     * This method is called when the user click on the [Save Configuration] button of the modal
     */
    saveConfigurationModal() {
      const currentOrder = this.orders[this.configModal.orderIndex];
      const fabric = currentOrder.fabrics[this.configModal.fabricIndex];

      let allowUpdateOrder = true;

      if (this.$auth.user().role === "admin") {
        const totalFiberPercentage =
          parseInt(fabric.fiber_1_percentage || 0) +
          parseInt(fabric.fiber_2_percentage || 0) +
          parseInt(fabric.fiber_3_percentage || 0);

        if (totalFiberPercentage !== 100) {
          allowUpdateOrder = false;
          toastr.error("Total of fiber percentage must be equal to 100");
        }
      }

      if (allowUpdateOrder) {
        currentOrder.currency = fabric.default_currency;
        currentOrder.fabrics.forEach((f) => {
          f.default_currency = fabric.default_currency;
        });
        this.configModal = {
          displayed: false,
          fabricIndex: null,
          orderIndex: null,
        };
        hide_modal("#configuration-modal");
      }
    },

    /**
     * When the modal emits an 'component-updated'
     * event, it is handled by this method
     */
    handleModalUpdate() {
      this.hideConfigurationModal();
    },

    getJerseyLengthEstimationInMeter(fabric) {
      let defaultWeight = parseFloat(fabric.default_weight);
      let meterPerKg = 0;
      if(fabric.knit_type === 'open') {
        meterPerKg = 1000 / ((parseFloat(fabric.default_width) + 2) * defaultWeight * 0.0232);
      } else {
        meterPerKg = 1000 / (parseFloat(fabric.default_width) * 2 * defaultWeight * 0.0232);
      }
      return fabric.amount * meterPerKg;
    }
  },
};
</script>

<style scoped>
.form-control {
  height: 42px;
  border-radius: 4px;
}
tfoot tr td {
  font-weight: bold;
}
</style>
